import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router";
import {useHistory} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import CredentialsForm from "@/components/Form/Form";
import CredentialsHeader from "@/components/header/Header";
import ButtonContainer from "@/components/button-container";

import { genericService } from "@/services/GenericService";

import { tryCatch } from "@/utils/try-catch";
import { FormError } from "@/models/site-credentials/common/FormError";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import SimpleErrorMessage from "@/components/error-message/new-error-message";

type WelcomeBackForm = {
	continue: boolean;
	returnUrl: string | undefined;
}

type WelcomeBackFormPropertiesResponse = {
	email: string;
	organization: string;
	date: string;
};

type WelcomeBackFormResponse = {
	nextStep: string;
	properties: WelcomeBackFormPropertiesResponse;
};

type Form = WelcomeBackForm & FormError;

export default function WelcomeBack() {
	const { t } = useTranslation();

	const history = useHistory();

	let params = useParams<{ clientId: string }>();
	const clientId = params.clientId;
	const returnUrl = new URLSearchParams(useLocation().search).get("returnUrl");
	const [loading, setLoading] = useState(false);
	const { handleResponse } = useApiResponseHandler();

	const formState = useForm<Form>({
		mode: "onSubmit",
		reValidateMode: "onSubmit",
	});

	const [userData, setUserData] = useState<WelcomeBackFormPropertiesResponse[]>();

	useEffect(() => {
		const fetchSsoSessionInfo = async () => {
			const { data: response, error } = await tryCatch(genericService.get<WelcomeBackFormPropertiesResponse[]>("/api/sso/session-info"));

			if (error || response.kind !== "success") {
				history.push("/auth/" + clientId);
				return;
			}

			setUserData(response.value);
		};

		fetchSsoSessionInfo();
	}, []);

	const nextStep = "/api/sso/welcome-back/" + clientId;

	const {
		handleSubmit,
		setError,
		setValue,
		clearErrors,
		formState: { errors },
	} = formState;

	const onSubmit: SubmitHandler<WelcomeBackForm> = async (data) => {
		setLoading(true);

		const { data: response, error } = await tryCatch(genericService.post<WelcomeBackFormResponse, WelcomeBackForm>(
			nextStep,
			{
				continue: data.continue,
				returnUrl: returnUrl || undefined
			}
		));

		handleResponse(
			response,
			{
				setError
			},
			error
		);

		setLoading(false);
	};

	const handleChooseOption = (value: boolean) => {
		console.log("Chosen option:", value ? "Continue with SSO" : "Logout and use another account");
		setValue("continue", value);
		clearErrors();
		handleSubmit(onSubmit)();
	};

	return (
		<CredentialsForm
			id="WelcomeBackForm"
			autoComplete="on"
			form={formState}
			onSubmit={(e) => {
				clearErrors();
				handleSubmit(onSubmit, (errors) => {
					console.log("Form errors:", errors);
				})(e);
			}}
		>
			<CredentialsHeader
				title={t("WelcomeBack.title")}
				subtitle={t("WelcomeBack.subtitle")}
				showLoading={loading}
			/>

			<div className="flex gap-1 flex-col"> 
				{
					userData && userData.map((user, index) => (
						<div key={index} className="flex items-start justify-between rounded-xl border border-slate-200 bg-slate-50 p-4 gap-2 w-full">
							<div className="flex items-start gap-4">
								<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-6 w-6"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										strokeWidth={1.8}
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M15.75 19.5a7.5 7.5 0 10-7.5 0m7.5 0a9 9 0 01-7.5 0m7.5 0a9 9 0 01-7.5 0M15 9a3 3 0 11-6 0 3 3 0 016 0z"
										/>
									</svg>
								</div>

								<div className="text-sm text-slate-700">
									<p className="text-base leading-none text-slate-800" style={{ overflowWrap: 'anywhere' }}>
										{user.email}
									</p>
									<p className="leading-6 mt-2">
										<span className="text-slate-500">{t("WelcomeBack.organizationLabel")}</span>{" "}
										<span className="font-medium text-slate-700">{user.organization}</span>
									</p>
									<p className="leading-6">
										<span className="text-slate-500">{t("WelcomeBack.date")}</span>{" "}
										<span className="font-medium text-slate-700">{user.date}</span>
									</p>
								</div>
							</div>

							<span className="rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white">
								SSO
							</span>
						</div>
					))
				}
			</div>

			{errors.submitError?.message &&
				<SimpleErrorMessage
					id={"error-message"}
					error={errors.submitError?.message}
				/>
			}

			<ButtonContainer className="gap-0">
				<Button
					variant={"link"}
					type="button"
					className="pb-0"
					onClick={() => handleChooseOption(false)}
				>
					{t("WelcomeBack.logout")}
				</Button>

				<Button
					variant={"default"}
					type="button"
					onClick={() => handleChooseOption(true)}
				>
					{t("WelcomeBack.buttonText")}
				</Button>
			</ButtonContainer>
		</CredentialsForm>
	);
}